import React, { useLayoutEffect, useRef } from "react";
import { LayoutGrid, StickyNote } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primaryAlpha } from "shared/theme/trodoColors";
import {
  MAX_DASHBOARD_CONTEXT_BOARDS,
  MAX_DASHBOARD_CONTEXT_CARDS,
} from "features/dashboard/constants/dashboardContextLimits";

const BORDER = theme.border;

function sectionLabel(text) {
  return (
    <div
      className="px-2.5 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ color: theme.textMuted }}
    >
      {text}
    </div>
  );
}

/**
 * Board/card picker for chat.
 * When `flatRows` is set: single thin list (inline @) with optional keyboard highlight.
 * Otherwise: legacy sectioned layout (e.g. @ button with search).
 */
export default function ChatAtContextPopover({
  placement = "above",
  anchor = "textarea",
  filterQuery,
  showSearchField,
  pickerSearch,
  onPickerSearchChange,
  pickerBoardsLoading,
  filteredPickerBoards,
  filteredFlatPickerCards,
  pickerCardsStillLoading,
  boardAddDisabled,
  cardAddDisabled,
  attachments: _attachments, // kept for call-site compatibility
  onAddBoard,
  onAddCard,
  /** Pre-flattened rows for minimal inline UI (order must match keyboard nav in ChatPage) */
  flatRows = null,
  /** Highlighted row index for keyboard nav; -1 = none */
  highlightIndex = -1,
}) {
  const fq = (filterQuery || "").trim().toLowerCase();

  const positionClass =
    anchor === "button-right"
      ? "left-full ml-2 top-1/2 -translate-y-1/2"
      : placement === "above"
        ? "bottom-full left-0 mb-1.5"
        : "top-full left-0 mt-1.5";

  const noBoardMatches = !pickerBoardsLoading && filteredPickerBoards.length === 0;
  const noCardMatches = !pickerCardsStillLoading && filteredFlatPickerCards.length === 0;

  const rowRefs = useRef([]);

  useLayoutEffect(() => {
    if (highlightIndex < 0 || !flatRows?.length) return;
    const el = rowRefs.current[highlightIndex];
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, flatRows]);

  const minimalShell = Boolean(flatRows);

  if (minimalShell) {
    return (
      <div
        className={`absolute z-[80] w-[min(100vw-2rem,18rem)] max-h-[min(38vh,13.5rem)] overflow-hidden flex flex-col rounded-lg border text-left shadow-md ${positionClass}`}
        style={{
          borderColor: BORDER,
          backgroundColor: theme.bgRaised,
          boxShadow: "0 10px 28px rgba(0,0,0,0.07)",
        }}
        role="listbox"
        aria-label="Attach board or card"
      >
        {pickerBoardsLoading && (
          <div className="px-2.5 py-2 text-[11px]" style={{ color: theme.textMuted }}>
            Loading boards…
          </div>
        )}

        {!pickerBoardsLoading && (
          <div className="overflow-y-auto overflow-x-hidden py-0.5 max-h-[min(36vh,12.5rem)]">
            {flatRows.length === 0 && !pickerCardsStillLoading && (
              <div className="px-2.5 py-2 text-[11px]" style={{ color: theme.textMuted }}>
                {fq ? `No matches for “${filterQuery}”` : "No boards or cards yet"}
              </div>
            )}

            {pickerCardsStillLoading && flatRows.length === 0 && (
              <div className="px-2.5 py-2 text-[11px]" style={{ color: theme.textMuted }}>
                Loading cards…
              </div>
            )}

            {flatRows.map((row, i) => {
              const isHi = i === highlightIndex;
              if (row.kind === "board") {
                const b = row.board;
                const disabled = boardAddDisabled;
                return (
                  <button
                    key={row.key}
                    id={`chat-at-opt-${i}`}
                    type="button"
                    role="option"
                    aria-selected={isHi}
                    disabled={disabled}
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    title={disabled ? `Only ${MAX_DASHBOARD_CONTEXT_BOARDS} board allowed` : undefined}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors outline-none"
                    style={{
                      backgroundColor: isHi ? primaryAlpha(0.1) : "transparent",
                      color: disabled ? theme.textMuted : theme.textPrimary,
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled && !isHi) e.currentTarget.style.backgroundColor = theme.bgOverlay;
                    }}
                    onMouseLeave={(e) => {
                      if (!isHi) e.currentTarget.style.backgroundColor = "transparent";
                      else e.currentTarget.style.backgroundColor = primaryAlpha(0.1);
                    }}
                    onClick={() => !disabled && onAddBoard(b)}
                  >
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded"
                      style={{
                        backgroundColor: primaryAlpha(0.12),
                        color: theme.primaryLight,
                      }}
                    >
                      <LayoutGrid size={13} strokeWidth={2} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12px] font-medium leading-tight">{b.name || "Untitled"}</span>
                    <span className="shrink-0 text-[10px] tabular-nums" style={{ color: theme.textMuted }}>
                      Board
                    </span>
                  </button>
                );
              }
              const { board, card, boardId } = row;
              const disabled = cardAddDisabled;
              return (
                <button
                  key={row.key}
                  id={`chat-at-opt-${i}`}
                  type="button"
                  role="option"
                  aria-selected={isHi}
                  disabled={disabled}
                  ref={(el) => {
                    rowRefs.current[i] = el;
                  }}
                  title={disabled ? `Up to ${MAX_DASHBOARD_CONTEXT_CARDS} cards allowed` : undefined}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors outline-none"
                  style={{
                    backgroundColor: isHi ? primaryAlpha(0.1) : "transparent",
                    color: disabled ? theme.textMuted : theme.textPrimary,
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled && !isHi) e.currentTarget.style.backgroundColor = theme.bgOverlay;
                  }}
                  onMouseLeave={(e) => {
                    if (!isHi) e.currentTarget.style.backgroundColor = "transparent";
                    else e.currentTarget.style.backgroundColor = primaryAlpha(0.1);
                  }}
                  onClick={() => !disabled && onAddCard(boardId, card)}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded"
                    style={{
                      backgroundColor: primaryAlpha(0.1),
                      color: theme.textSecondary,
                    }}
                  >
                    <StickyNote size={13} strokeWidth={2} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium leading-tight">
                    {card.title || "Untitled card"}
                  </span>
                  <span className="shrink-0 max-w-[5.5rem] truncate text-[10px] text-right" style={{ color: theme.textMuted }}>
                    {board.name || "Board"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`absolute z-[80] w-[min(100vw-2rem,18rem)] max-h-[min(70vh,20rem)] max-w-[min(18rem,calc(100vw-5rem))] overflow-hidden flex flex-col rounded-lg border text-left shadow-md ${positionClass}`}
      style={{
        borderColor: BORDER,
        backgroundColor: theme.bgRaised,
        boxShadow: "0 10px 28px rgba(0,0,0,0.07)",
      }}
      role="listbox"
      aria-label="Attach board or card"
    >
      {showSearchField && (
        <div className="px-2 py-1.5 border-b shrink-0" style={{ borderColor: BORDER }}>
          <input
            type="search"
            value={pickerSearch}
            onChange={(e) => onPickerSearchChange(e.target.value)}
            placeholder="Search boards and cards…"
            className="w-full text-[13px] rounded-lg px-2.5 py-1.5 outline-none transition-shadow focus:ring-2"
            style={{
              border: `1px solid ${BORDER}`,
              backgroundColor: theme.bgBase,
              color: theme.textPrimary,
              boxShadow: "none",
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {!showSearchField && fq && (
        <div className="px-2.5 py-1 text-[10px] border-b shrink-0" style={{ borderColor: BORDER, color: theme.textMuted }}>
          Filter: <span style={{ color: theme.textPrimary }}>{filterQuery || "…"}</span>
        </div>
      )}

      <div className="overflow-y-auto flex-1 min-h-0 py-0.5">
        {pickerBoardsLoading && (
          <div className="px-2.5 py-2 text-[12px]" style={{ color: theme.textMuted }}>
            Loading boards…
          </div>
        )}

        {!pickerBoardsLoading && (
          <>
            {sectionLabel("Boards")}
            {filteredPickerBoards.map((b) => {
              const bid = parseInt(String(b.id), 10);
              if (Number.isNaN(bid) || bid < 1) return null;
              return (
                <button
                  key={`board-${bid}`}
                  type="button"
                  disabled={boardAddDisabled}
                  title={boardAddDisabled ? `Only ${MAX_DASHBOARD_CONTEXT_BOARDS} board allowed` : undefined}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors rounded-md mx-0.5"
                  style={{
                    color: boardAddDisabled ? theme.textMuted : theme.textPrimary,
                  }}
                  onMouseEnter={(e) => {
                    if (!boardAddDisabled) e.currentTarget.style.backgroundColor = theme.bgOverlay;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onClick={() => !boardAddDisabled && onAddBoard(b)}
                >
                  <LayoutGrid size={14} className="shrink-0 opacity-70" strokeWidth={2} aria-hidden />
                  <span className="truncate flex-1 font-medium">{b.name || "Untitled"}</span>
                  <span className="text-[10px] shrink-0" style={{ color: theme.textMuted }}>
                    Board
                  </span>
                </button>
              );
            })}
            {noBoardMatches && (
              <div className="px-2.5 pb-1.5 text-[11px]" style={{ color: theme.textMuted }}>
                {fq ? `No boards match “${filterQuery}”` : "No boards"}
              </div>
            )}

            {sectionLabel("Cards")}
            {pickerCardsStillLoading && (
              <div className="px-2.5 py-1 text-[11px]" style={{ color: theme.textMuted }}>
                Loading cards…
              </div>
            )}
            {!pickerCardsStillLoading &&
              filteredFlatPickerCards.map(({ boardId, board, card }) => (
                <button
                  key={`card-${boardId}-${card.id}`}
                  type="button"
                  disabled={cardAddDisabled}
                  title={cardAddDisabled ? `Up to ${MAX_DASHBOARD_CONTEXT_CARDS} cards allowed` : undefined}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors rounded-md mx-0.5"
                  style={{
                    color: cardAddDisabled ? theme.textMuted : theme.textPrimary,
                  }}
                  onMouseEnter={(e) => {
                    if (!cardAddDisabled) e.currentTarget.style.backgroundColor = theme.bgOverlay;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onClick={() => !cardAddDisabled && onAddCard(boardId, card)}
                >
                  <StickyNote size={14} className="shrink-0 opacity-70" strokeWidth={2} aria-hidden />
                  <span className="min-w-0 flex-1 text-[12px] font-medium truncate">{card.title || "Untitled card"}</span>
                  <span className="text-[10px] max-w-[5rem] truncate shrink-0" style={{ color: theme.textMuted }}>
                    {board.name || "Board"}
                  </span>
                </button>
              ))}
            {!pickerCardsStillLoading && noCardMatches && (
              <div className="px-2.5 pb-1.5 text-[11px]" style={{ color: theme.textMuted }}>
                {fq ? `No cards match “${filterQuery}”` : "No cards"}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
